import * as React from 'react';

interface Props {
  onToken: (token: string) => void;
  onExpire?: () => void;
}

/**
 * How long to wait for Cloudflare to attach its challenge iframe before
 * declaring the widget dead. The iframe appears within a beat of render() even
 * for interactive challenges — this is NOT a deadline for the human to solve
 * anything, so a slow user is never punished by it.
 */
const IFRAME_WATCHDOG_MS = 10000;
const IFRAME_POLL_MS = 200;

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      // Already loaded (or loading). If `turnstile` is already on window, resolve immediately.
      if (window.turnstile) return resolve();
    } else {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onerror = () => reject(new Error('turnstile script failed to load'));
      document.head.appendChild(s);
    }
    // Poll until `turnstile` shows up on window (CF script doesn't fire onload reliably).
    const start = Date.now();
    const id = setInterval(() => {
      if (window.turnstile) { clearInterval(id); resolve(); }
      else if (Date.now() - start > 8000) { clearInterval(id); reject(new Error('turnstile timeout')); }
    }, 100);
  });
}

export function TurnstileWidget({ onToken, onExpire }: Props) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const widgetIdRef = React.useRef<string | null>(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '';

  // A captcha that never produces a token leaves every consuming form's submit
  // button disabled forever (LoginPage, ForgotPasswordPage, AdminLoginPage and
  // SetPasswordPage all gate on `!turnstileToken`). Silently swallowing that —
  // as this component used to — locks the user out of sign-in, password reset
  // and invite acceptance with no explanation. Surface it and offer a retry
  // instead. Failing CLOSED is still correct; failing closed *and silent* isn't.
  const [failed, setFailed] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);

  // Keep the latest callbacks in refs so the render effect does NOT depend on
  // their identity. Parents pass inline closures (e.g. onExpire={() => ...})
  // whose identity changes on every render; if the effect depended on them it
  // would tear the widget down and re-create it on every keystroke (email /
  // password are state on the login form), producing the "captcha keeps
  // reloading and never returns a token" loop. The widget must mount ONCE and
  // stay put — a full reset on retry is done by the parent via `key`.
  const onTokenRef = React.useRef(onToken);
  const onExpireRef = React.useRef(onExpire);
  React.useEffect(() => {
    onTokenRef.current = onToken;
    onExpireRef.current = onExpire;
  });

  React.useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    let watchdog: ReturnType<typeof setInterval> | null = null;
    const stopWatchdog = () => {
      if (watchdog !== null) { clearInterval(watchdog); watchdog = null; }
    };

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        // Guard against a double-render onto the same element (CF throws
        // "widget already rendered" otherwise).
        if (widgetIdRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'auto',
          callback: (token: string) => { stopWatchdog(); setFailed(false); onTokenRef.current(token); },
          'error-callback': () => { stopWatchdog(); setFailed(true); },
          'timeout-callback': () => { stopWatchdog(); setFailed(true); },
          'expired-callback': () => onExpireRef.current?.(),
        });

        // Cloudflare sometimes returns from render() having attached nothing at
        // all — the container ends up holding only the hidden response input,
        // no challenge iframe, and no callback ever fires. Observed live on
        // 2026-07-30. Watch for the iframe; if it never shows, the widget can
        // never yield a token, so say so rather than sitting there looking busy.
        const started = Date.now();
        watchdog = setInterval(() => {
          if (cancelled) { stopWatchdog(); return; }
          if (containerRef.current?.querySelector('iframe')) { stopWatchdog(); return; }
          if (Date.now() - started >= IFRAME_WATCHDOG_MS) { stopWatchdog(); setFailed(true); }
        }, IFRAME_POLL_MS);
      })
      .catch(() => { if (!cancelled) setFailed(true); });

    return () => {
      cancelled = true;
      stopWatchdog();
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, attempt]);

  if (!siteKey) return null;
  return (
    <div className="mt-4">
      <div ref={containerRef} className="cf-turnstile" />
      {failed && (
        <div
          role="alert"
          className="mt-2 text-[13px] text-ink/70 dark:text-white/70 bg-amber-500/10 ring-1 ring-amber-500/20 rounded-2xl px-3 py-2"
        >
          <p>
            Couldn't load the security check. An ad-blocker, privacy extension or
            network proxy may be blocking it.
          </p>
          <button
            type="button"
            onClick={() => { setFailed(false); setAttempt((n) => n + 1); }}
            className="mt-1 font-semibold underline underline-offset-2 hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
